import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import {
  defaultAttachmentOderDesOptions,
  defaultStatusOptions,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Col, Form, Row, Select } from 'antd';
import { FormProps } from 'antd/lib';
import { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

export type AttachmentOrderValues = {
  deductionDescription: Option<string> | null;
  deductionMethod: string;
  attachmentOrderStatus: Option<string> | null;
  attachmentOrderPriority: string;
  attachmentOrderEffectiveDate: Moment | null;
  attachmentOrderEndDate: Moment | null;
  protectedMinimum: string;
  protectedMinType: 'percentage' | 'amount';
  deductionRate: string;
  marginFee: string;
  orderRef: string;
  totalAttachment: string;
};

export type AttachmentOrderFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => AttachmentOrderValues;
  resetFields: () => void;
};

type AttachmentOrderFormProps = {
  formRef?: Ref<AttachmentOrderFormRef>;
  initialValues?: AttachmentOrderValues;
};

const AttachmentOrderForm: React.FC<AttachmentOrderFormProps> = ({
  formRef,
  initialValues,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm<AttachmentOrderValues>();
  const protectedMinType = Form.useWatch('protectedMinType', form);
  // Protected minimum is GBP when the addon selector says amount, % otherwise.
  const ProtectedMinInput =
    protectedMinType === 'amount' ? AppCurrencyInput : AppRateInput;

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="text-[18px] font-bold mb-[40px]">
        {messages['common.attachmentOrders']}
      </div>
      <Row gutter={24}>
        <Col span={24}>
          <Form.Item
            name="deductionDescription"
            label={messages['common.deductionDescription']}
          >
            <AppSelect
              options={defaultAttachmentOderDesOptions}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="deductionMethod"
            label={messages['common.deductionMethod']}
          >
            <AppInput disabled />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="attachmentOrderStatus"
            label={messages['common.status']}
          >
            <AppSelect
              options={defaultStatusOptions}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="attachmentOrderPriority"
            label={messages['common.priority']}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="attachmentOrderEffectiveDate"
            label={messages['common.effectiveDate']}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="attachmentOrderEndDate"
            label={messages['common.endDate']}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="protectedMinimum"
            label={messages['common.protectedMinimum']}
          >
            <ProtectedMinInput
              addonBefore={
                <Form.Item name="protectedMinType" noStyle initialValue="percentage">
                  <Select style={{ width: 60 }}>
                    <Select.Option value="percentage">%</Select.Option>
                    <Select.Option value="amount">£</Select.Option>
                  </Select>
                </Form.Item>
              }
              placeholder={messages['form.enter']}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="deductionRate"
            label={messages['common.deductionOrRate']}
          >
            <AppCurrencyInput
              placeholder={messages['form.enter']}
              prefix={showCurrencyByCountry(payrollCountry)}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="marginFee" label={messages['common.marginFee']}>
            <AppCurrencyInput
              placeholder={messages['form.enter']}
              prefix={showCurrencyByCountry(payrollCountry)}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="orderRef" label={messages['common.orderRef']}>
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="totalAttachment"
            label={messages['common.totalAttachment']}
            rules={[
              {
                // Optional (empty = no cap), but a value entered must be above 0.
                validator: async (_, value) => {
                  if (value !== '' && value != null && Number(value) <= 0) {
                    throw new Error(messages['form.mustBeGreaterThanZero']);
                  }
                },
              },
            ]}
          >
            <AppCurrencyInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default AttachmentOrderForm;
